import React, { useState, useEffect, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';
import DocumentLoading from './document-loading';
import DocumentError from './document-error';
import { isInApp } from '../../../common/function';
import { COLOR } from '../../../common/constant';
import UButton from "../../../UButton";

const FileDoc = memo(({ url, type, onDownload, rotation }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let blobUrl = null;
    const generatePreviewUrl = () => {
      try {
        if (['xlsx', 'xls'].includes(type)) {
          return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
        } else if (type !== 'pdf') {       // if pdf not load then remove this condition 
          return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
        }
      } catch (err) {
        console.error("Error generating preview URL:", err);
      }
      return null;
    };

    if (isInApp()) {
      setShowFallback(true);
      setLoading(false);
      return;
    }

    if (type === 'pdf') {  // if pdf not load then remove this if condition 
      fetch(url).then(res => res.blob()).then(blob => {
        blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPreviewUrl(blobUrl + '#toolbar=0&navpanes=0');
        setLoading(false);
      }).catch(() => { setError("Unable to load PDF"); setLoading(false); });
    } else {
      const parsedUrl = generatePreviewUrl();
      parsedUrl ? setPreviewUrl(parsedUrl) : setError("Unable to generate preview for this document");
      setLoading(false);
    }

    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url, type]);

  if (loading) return <DocumentLoading />;
  if (error) {
    return <DocumentError message={error} />;
  }

  if (showFallback) {
    return (
      <div className="u_m-auto u_text-center u_color-white">
        <Icon.FilePreview fill={COLOR.u_white} size={48} />
        <p className="u_font-14 u_fw-600 u_mt-15">{type?.toUpperCase()} Document</p>
        <p className="u_font u_my-10">Preview is not available in this view</p>
        {onDownload && <UButton variant="blue" type="Download" onClick={onDownload} />}
      </div>
    );
  }

  return (
    <div className="u_fv-doc" style={{ transform: `rotate(${rotation || 0}deg)` }}>
      <iframe 
        src={previewUrl} 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        title="Office Document Preview"
      >
        This browser does not support embedded frames.
      </iframe>
      {!['xlsx', 'xls', 'pdf'].includes(type) && <div className="u_fv-doc-right"/>}
    </div>
  );
});

FileDoc.propTypes = {
  url: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onDownload: PropTypes.func
};

export default FileDoc; 